import React, { useState } from 'react';
// Import NavLink to style the active link
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../Context/adminAuthContext';

// --- SVG Icons ---

// Hamburger Icon
const MenuIcon = (props) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24" 
    strokeWidth={1.5} 
    stroke="currentColor" 
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
  </svg>
);

// Close Icon
const CloseIcon = (props) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24" 
    strokeWidth={1.5} 
    stroke="currentColor" 
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// --- Main Layout Component ---

const AdminLayout = () => {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const hasPermission = (allowedRoles = []) => {
    if (!admin || !admin.roles) return false;
    // The Super Admin can see everything
    if (admin.roles.includes('super_admin')) return true;
    return admin.roles.some(role => allowedRoles.includes(role));
  };
  const navLinkClasses = "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200";
  const activeLinkClasses = "bg-indigo-600 text-white font-semibold shadow-md";
  const inactiveLinkClasses = "text-gray-300 hover:bg-gray-700 hover:text-white";


const renderNavLinks = () => (
  <nav className="flex-1 px-4 py-6 space-y-2">
    {/* Dashboard */}
    <NavLink
      to="/admin/dashboard"
      className={({ isActive }) =>
        `${navLinkClasses} ${isActive ? activeLinkClasses : inactiveLinkClasses}`
      }
    >
      <span>Dashboard</span>
    </NavLink>

    {/* Tasker Management */}
    {hasPermission(['tasker_manager']) && (
      <NavLink
        to="/admin/taskers"
        className={({ isActive }) =>
          `${navLinkClasses} ${isActive ? activeLinkClasses : inactiveLinkClasses}`
        }
      >
        <span>Tasker Management</span>
      </NavLink>
    )}

    {/* User Management */}
    {hasPermission(['user_manager']) && (
      <NavLink
        to="/admin/users"
        className={({ isActive }) =>
          `${navLinkClasses} ${isActive ? activeLinkClasses : inactiveLinkClasses}`
        }
      >
        <span>User Management</span>
      </NavLink>
    )}

    {/* Disputes */}
    {hasPermission(['dispute_team']) && (
      <NavLink
        to="/admin/disputes"
        className={({ isActive }) =>
          `${navLinkClasses} ${isActive ? activeLinkClasses : inactiveLinkClasses}`
        }
      >
        <span>Disputes</span>
      </NavLink>
    )}

    {/* Support */}
    {hasPermission(['support_team', 'support_finance', 'support_accounts']) && (
      <NavLink
        to="/admin/support"
        className={({ isActive }) =>
          `${navLinkClasses} ${isActive ? activeLinkClasses : inactiveLinkClasses}`
        }
      >
        <span>Support Tickets</span>
      </NavLink>
    )}

    {/* Admin Management */}
    {hasPermission(['super_admin']) && (
      <NavLink
        to="/admin/admin-management"
        className={({ isActive }) =>
          `${navLinkClasses} ${isActive ? activeLinkClasses : inactiveLinkClasses}`
        }
      >
        <span>Admin Management</span>
      </NavLink>
    )}
  </nav>
);

  return (
    <div className="flex min-h-screen">
      
      {/* --- Mobile Overlay --- */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-20 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      {/* --- Sidebar --- */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-30 w-64 bg-gray-900 text-gray-100
          flex flex-col shadow-lg
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-2xl font-bold text-white">Admin Panel</h3>
          <button 
            onClick={() => setIsSidebarOpen(false)} 
            className="md:hidden p-1 text-gray-400 rounded-lg hover:bg-gray-700"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>
        
        {/* Nav Links */}
        {renderNavLinks()}
      </aside>

      {/* --- Main Content Area --- */}
      <div className="flex-1 flex flex-col md:ml-64">
        
        {/* --- Top Header Bar --- */}
        <header className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 text-gray-600 rounded-lg hover:bg-gray-100 md:hidden"
            >
              <MenuIcon className="w-6 h-6" />
            </button>

            {/* Spacer */}
            <div className="flex-1"></div>

            {/* User Info & Logout */}
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700 hidden sm:block">
                Welcome, {admin?.name}
              </span>
              <button 
                onClick={handleLogout}
                className="
                  bg-indigo-50 hover:bg-indigo-100 text-indigo-600 
                  text-sm font-semibold py-2 px-4 rounded-lg 
                  transition-colors duration-200 shadow-sm border border-indigo-200
                "
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* --- Page Content --- */}
        <main className="flex-1 overflow-y-auto bg-gray-100">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;